import { Form,Select,Typography } from "../../../../../shared/frontend/ui";
import React from "react";
import { allStudiosTexts } from "../app/i18n/allStudiosTexts";
import type { LocalizedText } from "../app/i18n/i18n";
import { useI18n } from "../app/i18n/useI18n";

export type StudioOption = {
  value: string;
  label: React.ReactNode;
  disabled?: boolean;
  title?: string;
};

export type StudioScopeFieldTexts = LocalizedText;

export interface StudioScopeFieldProps {
  /** Nur anzeigen wenn Mandant mehr als ein Studio hat */
  show: boolean;
  /** Form field name (z. B. "allowedStudioIds", "studioIds") */
  name: string;
  /** true = Eintrag gilt für alle Studios (leere Zuordnung); dann bei isRestrictedUser read-only */
  isEntryAllStudios: boolean;
  optionsOnlyAllowed: StudioOption[];
  /** Optional: z. B. studioOptionsWithDisabled von useStudioUniqueness (bereits vergebene Studios disabled) */
  optionsOverride?: StudioOption[];
  isRestrictedUser: boolean;
  label: StudioScopeFieldTexts;
  tooltip?: StudioScopeFieldTexts;
  placeholder?: StudioScopeFieldTexts;
  /** "multiple" = Mehrfachauswahl (Standard), undefined/weglassen = Einzelauswahl (ein Studio oder leer = alle) */
  mode?: "multiple";
  required?: boolean;
  showSearch?: boolean;
  maxTagCount?: "responsive" | number;
  style?: React.CSSProperties;
  allowClear?: boolean;
  onChange?: (value: string | string[]) => void;
  /** Z. B. rules für Form.Item */
  rules?: React.ComponentProps<typeof Form.Item>["rules"];
}

/**
 * Zentrale Studio-Scope-Auswahl für Einstellungs-Drawer.
 * - Bei nur einem Studio (show=false): nichts rendern.
 * - Bei „alle Studios“-Eintrag und eingeschränktem User: read-only „alle Studios“.
 * - Sonst: Multi- oder Single-Select nur mit zugreifbaren Studios.
 */
export function StudioScopeField({
  show,
  name,
  isEntryAllStudios,
  optionsOnlyAllowed,
  isRestrictedUser,
  label,
  tooltip,
  placeholder = allStudiosTexts.placeholder,
  mode = "multiple",
  required,
  showSearch,
  maxTagCount = "responsive",
  style,
  allowClear = true,
  onChange,
  rules,
  optionsOverride
}: StudioScopeFieldProps) {
  const { text } = useI18n();

  if (!show) return null;

  const showReadOnly = isEntryAllStudios && isRestrictedUser;
  const effectiveTooltip = showReadOnly ? undefined : (tooltip ? text(tooltip) : undefined);
  const selectOptions = optionsOverride ?? optionsOnlyAllowed;

  return (
    <Form.Item
      label={text(label)}
      tooltip={effectiveTooltip}
      required={required}
      style={style}
    >
      {showReadOnly ? (
        <Typography.Text type="secondary">{text(allStudiosTexts.label)}</Typography.Text>
      ) : (
        <Form.Item name={name} noStyle rules={rules}>
          <Select
            {...(mode === "multiple" ? { mode: "multiple" as const, maxTagCount } : {})}
            allowClear={allowClear}
            placeholder={text(placeholder)}
            options={selectOptions}
            style={{ width: "100%", ...style }}
            showSearch={showSearch}
            onChange={onChange}
            filterOption={
              showSearch
                ? (input, opt) => (opt?.label ?? "").toString().toLowerCase().includes(input.toLowerCase())
                : undefined
            }
          />
        </Form.Item>
      )}
    </Form.Item>
  );
}
